"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useState } from "react";
import { GamePageModal } from "../GamePageModal";

const FullScreenMedia = dynamic(
  () =>
    import("../FullScreenMedia/FullScreenMedia").then((m) => m.FullScreenMedia),
  {
    ssr: false,
  },
);

interface ArtGalleryModalProps {
  imageURLs: string[];
  gameTitle?: string;
  gameIcon?: string;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  gameId?: string;
}

export const ArtGalleryModal = ({
  imageURLs,
  gameTitle,
  gameIcon,
  isModalOpen,
  setIsModalOpen,
  gameId,
}: ArtGalleryModalProps) => {
  const { track } = useGamePageV3Analytics();
  const [isImgOpen, setIsImgOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    if (isModalOpen && gameId) {
      track({
        action: Action.VIEWED,
        extras: {
          id: "art-gallery-modal",
          gameId,
          gameName: gameTitle,
        },
      });
    }
  }, [isModalOpen, gameId, gameTitle, track]);

  const handleImageOpen = (index: number) => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-image",
          imageIndex: index.toString(),
          control: "ArtGalleryImage",
          controlType: ControlType.IMAGE,
          gameId,
          gameName: gameTitle,
        },
      });
    }
    setImgIndex(index);
    setIsImgOpen(true);
  };

  const handleImageHover = (index: number) => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-image-hover",
          imageIndex: index.toString(),
          control: "ArtGalleryImageHover",
          controlType: ControlType.IMAGE,
          gameId,
          gameName: gameTitle,
        },
      });
    }
    setImgIndex(index);
  };

  const handleCloseImg = () => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-close-fullscreen",
          control: "CloseArtGalleryModal",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
        },
      });
    }
    setIsImgOpen(false);
  };

  return (
    <>
      <GamePageModal
        title="Art Gallery Modal"
        gameTitle={gameTitle ?? "Game Title"}
        gameIcon={gameIcon}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        className="h-[95%] top-[52.5%] before:rounded-b-none [&>div]:rounded-b-none"
        gameId={gameId}
      >
        {/* Bento/Masonry Images Grid */}
        <div className="z-50 relative p-5 grid gap-5 overflow-y-auto auto-rows-[max-content] grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] md:auto-rows-[minmax(200px,240px)] lg:auto-rows-[minmax(220px,280px)] xl:auto-rows-[minmax(200px,320px)]">
          {imageURLs.map((imageUrl, index) => (
            <button
              type="button"
              key={imageUrl}
              className={cn(
                "relative overflow-hidden rounded-lg group cursor-pointer",
                index % 2 === 0 ? "md:row-span-2" : "md:row-span-1",
              )}
              onClick={() => handleImageOpen(index)}
              onMouseEnter={() => handleImageHover(index)}
              aria-label={`View ${gameTitle ?? "game"} art ${index + 1}`}
            >
              <Image
                src={imageUrl}
                alt={`${gameTitle ?? "Game"} Art ${index + 1}`}
                fill
                className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              />
            </button>
          ))}
        </div>
      </GamePageModal>

      <FullScreenMedia
        mediaURL={imageURLs[imgIndex]}
        gameTitle={gameTitle}
        handleCloseMedia={handleCloseImg}
        isMediaOpen={isImgOpen}
        gameId={gameId}
      />
    </>
  );
};
